import { useState } from 'react';
import { toast } from 'sonner';
import { Sparkle, Upload, X } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { runToolJob } from '@/lib/toolJob';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { TOOL_SEO, toolPath } from '@/lib/toolRoutes';
import ToolLayout, { Card, Field, Textarea, Select, PrimaryButton, UpsellBanner } from './_ToolLayout';
import EmailCaptureCard from '@/components/EmailCaptureCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLATFORMS = [
  ['trendyol', 'Trendyol'], ['hepsiburada', 'Hepsiburada'], ['amazon_tr', 'Amazon TR'],
  ['amazon_us', 'Amazon US'], ['amazon_de', 'Amazon DE'], ['etsy', 'Etsy'],
  ['walmart', 'Walmart'], ['ebay', 'eBay'], ['shopify', 'Shopify'], ['shopier', 'Shopier'], ['noon', 'Noon'],
];

export default function ReportAnalyticsTool() {
  const { t, language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const seo = TOOL_SEO.rapor[language] || TOOL_SEO.rapor.tr;
  useDocumentMeta({
    title: seo.title,
    description: seo.description,
    canonical: toolPath('rapor', language),
    alternates: { tr: toolPath('rapor', 'tr'), en: toolPath('rapor', 'en'), 'x-default': toolPath('rapor', 'tr') },
    ogKind: 'tool',
  });
  const [platform, setPlatform] = useState('trendyol');
  const [files, setFiles] = useState([]); // {name, content (text)}
  const [pasted, setPasted] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    const next = [...files];
    for (const f of list) {
      if (next.length >= 5) break;
      try {
        const text = await readFileSafe(f);
        next.push({ name: f.name, content: text });
      } catch (err) {
        toast.error(`${f.name} ${t('tools.rapor.read_fail')}`);
      }
    }
    setFiles(next);
    e.target.value = '';
  };

  const submit = async () => {
    const summary = buildSummary({ files, pasted });
    if (!summary || summary.length < 30) { toast.error(t('tools.rapor.required')); return; }
    setLoading(true);
    setResult(null);
    try {
      const data = await runToolJob(`${API}/tools/report-analytics`, { platform, data_summary: summary }, getAuthHeaders());
      setResult(data);
      window.scrollTo({ top: document.getElementById('an-result')?.offsetTop || 0, behavior: 'smooth' });
    } catch (e) {
      const msg = e?.response?.data?.detail || t('tools.rapor.fail');
      toast.error(typeof msg === 'string' ? msg : t('tools.rapor.fail'));
    } finally { setLoading(false); }
  };

  return (
    <ToolLayout
      testId="report-analytics-tool"
      tourId="analytics"
      title={t('tools.rapor.title')}
      subtitle={t('tools.rapor.subtitle')}
    >
      <Card className="mb-6">
        <Field label={t('tools.rapor.platform')}>
          <Select data-testid="an-platform" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORMS.map(([v, l]) => <option key={v} value={v} className="bg-[#0F1B36]">{l}</option>)}
          </Select>
        </Field>

        <Field label={t('tools.rapor.files')} hint={t('tools.rapor.files.hint')}>
          <label className="flex items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed border-white/15 hover:border-[#3CD9B8]/40 cursor-pointer text-white/70 text-sm">
            <Upload size={18} /> {t('tools.rapor.files.btn')}
            <input type="file" multiple accept=".csv,.txt,.tsv" className="hidden" onChange={handleFiles} data-testid="an-file-input" />
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-white/[0.04] rounded px-3 py-2">
                  <span className="truncate">{f.name} <span className="text-white/40 text-xs">({f.content.length.toLocaleString()} char)</span></span>
                  <button onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-white/50 hover:text-red-400" data-testid={`an-file-remove-${i}`}><X size={16} /></button>
                </li>
              ))}
            </ul>
          )}
        </Field>

        <Field label={t('tools.rapor.paste')} hint={t('tools.rapor.paste.hint')}>
          <Textarea data-testid="an-paste" rows={6} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={t('tools.rapor.paste.placeholder')} />
        </Field>

        <PrimaryButton onClick={submit} disabled={loading} data-testid="an-submit">
          {loading ? <><Sparkle size={18} className="animate-pulse" /> {t('tools.geo.submitting')}</> : <><Sparkle size={18} weight="fill" /> {t('tools.rapor.submit')}</>}
        </PrimaryButton>
      </Card>

      {result && <AnResult result={result} />}
    </ToolLayout>
  );
}

function readFileSafe(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || '').slice(0, 12000));
    r.onerror = () => reject(new Error('read fail'));
    r.readAsText(file);
  });
}

function buildSummary({ files, pasted }) {
  const parts = [];
  for (const f of files) parts.push(`=== ${f.name} ===\n${f.content}`);
  if (pasted.trim()) parts.push(`=== PASTED ===\n${pasted.trim()}`);
  return parts.join('\n\n');
}

function AnResult({ result }) {
  const { t } = useLanguage();
  const m = result.metrics || {};
  const stat = (label, val, suffix = '') => (
    <div className="p-3 rounded-lg bg-white/[0.04]">
      <div className="text-xs text-white/60 mb-1">{label}</div>
      <div className="text-lg font-bold">{val != null ? val.toLocaleString?.('tr-TR') ?? val : '—'}{suffix}</div>
    </div>
  );
  return (
    <div id="an-result" data-testid="an-result" className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">{result.magaza_adi || '—'}</h2>
            <p className="text-sm text-[#3CD9B8]">{result.platform}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stat(t('tools.rapor.metric.revenue'), m.net_ciro, ' ₺')}
          {stat(t('tools.rapor.metric.orders'), m.net_satis)}
          {stat(t('tools.rapor.metric.cancel'), m.iptal_orani, '%')}
          {stat(t('tools.rapor.metric.return'), m.iade_orani, '%')}
          {stat(t('tools.rapor.metric.rating'), m.satici_puani)}
          {stat(t('tools.rapor.metric.followers'), m.takipci)}
          {stat(t('tools.rapor.metric.basket'), m.ort_sepet, ' ₺')}
          {stat(t('tools.rapor.metric.commission'), m.komisyon_toplam, ' ₺')}
        </div>
      </Card>

      {(result.guclu_yonler?.length || result.zayif_yonler?.length) && (
        <Card>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2">{t('tools.rapor.strong')}</p>
              <ul className="space-y-1 text-sm">{(result.guclu_yonler || []).map((g, i) => <li key={i} className="text-white/80">+ {g}</li>)}</ul>
            </div>
            <div>
              <p className="text-xs text-red-400 uppercase tracking-wider mb-2">{t('tools.rapor.weak')}</p>
              <ul className="space-y-1 text-sm">{(result.zayif_yonler || []).map((z, i) => <li key={i} className="text-white/80">− {z}</li>)}</ul>
            </div>
          </div>
        </Card>
      )}

      {result.urun_performans?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">{t('tools.rapor.products')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-white/50 border-b border-white/10"><th className="py-2 px-2">{t('tools.rapor.col.product')}</th><th className="py-2 px-2">{t('tools.rapor.col.orders')}</th><th className="py-2 px-2">{t('tools.rapor.col.revenue')}</th><th className="py-2 px-2">{t('tools.rapor.col.cancel')}</th><th className="py-2 px-2">{t('tools.rapor.col.stock')}</th><th className="py-2 px-2">{t('tools.rapor.col.status')}</th></tr></thead>
              <tbody>
                {result.urun_performans.map((u, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 px-2">{u.isim}</td>
                    <td className="py-2 px-2">{u.satis}</td>
                    <td className="py-2 px-2">{u.ciro?.toLocaleString?.('tr-TR')} ₺</td>
                    <td className="py-2 px-2">{u.iptal_oran}%</td>
                    <td className="py-2 px-2">{u.stok}</td>
                    <td className="py-2 px-2 text-xs uppercase">{u.durum}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {result.uyarilar?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">{t('tools.rapor.alerts')}</h3>
          <div className="space-y-2">
            {result.uyarilar.map((u, i) => {
              const colors = { kritik: 'bg-red-500/5 border-red-500/20', uyari: 'bg-amber-500/5 border-amber-500/20', bilgi: 'bg-blue-500/5 border-blue-500/20' };
              return (
                <div key={i} className={`p-3 rounded-lg border ${colors[u.seviye] || 'bg-white/[0.04] border-white/10'}`}>
                  <div className="text-xs uppercase mb-1">{u.seviye}</div>
                  <div className="font-medium text-sm mb-1">{u.baslik}</div>
                  <p className="text-xs text-white/70">{u.aciklama}</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {result.odevler?.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">{t('tools.rapor.tasks')}</h3>
          <div className="space-y-2">
            {result.odevler.map((o, i) => (
              <div key={i} className="p-3 rounded-lg bg-white/[0.04] border border-white/5">
                <div className="flex items-center justify-between text-xs mb-1"><span className="text-[#3CD9B8] uppercase font-semibold">{o.oncelik}</span></div>
                <div className="font-medium text-sm mb-1">{o.baslik}</div>
                <p className="text-xs text-white/70 mb-1">{o.nasil}</p>
                <p className="text-xs text-[#3CD9B8]">{t('tools.comp.result.impact')} {o.etki}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <UpsellBanner />
      <EmailCaptureCard toolSlug="rapor-analitik" resultSnapshot={result} />
    </div>
  );
}
